/* global AccessEnablerUrl */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  BUILD_VERSION,
  callTealiumEvent,
  Events,
  getAccountAnalyticsEvents,
  getSegmentAuthTrackEvents,
  getSegmentMVPDEvents,
  handleAnalytics,
  setSegmentPageStartSource,
  setTealiumProfileId,
  setTealiumSourceAuth,
  TEALIUM_EVENT_PREFIX,
  TealiumOnboardingActions,
  TrackEvents,
} from '@foxcorp/delta-web-analytics';
import VideoProviderSearch from 'components/Auth/VideoProviderSearch';
import { helperGetApiKey } from 'helpers/ApiHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { noop } from 'helpers/GlobalHelper';
import {
  BUYTHROUGH_DATA_COOKIE_NAME,
  MVPD_COOKIE_NAME,
  SUCCESS_COOKIE_NAME,
  adobeAuthOptions,
  authMVPDLogout,
  getContentBlackoutProps,
  getCookiesBlockedProps,
  getDefaultAuthProps,
  getFailureProps,
  getGeoLocationErrorProps,
  getLoadingProps,
  getMvpdDirectSignInRedirectProps,
  getMVPDIframeProps,
  getMVPDIncompleteProps,
  getMVPDRetryProps,
  getMVPDSearchProps,
  getMVPDStartProps,
  getMVPDUpsellRedirectingProps,
  getNoSportsOnMobileProps,
  getNotEntitledProps,
  getPlaybackErrorProps,
  getPreviewPassWelcomeProps,
  getProfilePcsErrorProps,
  getProfileSignInProps,
  getSignInProps,
  getUnlockUpsellProps,
  getSuccessProps,
  getUpsellConfirmProps,
  getUpsellErrorProps,
} from 'helpers/AuthHelper';
import {
  getAuthBackgroundImage,
  getAuthSignInBackgroundImage,
  getAuthNetworksConfig,
  geolocationEnabled,
  isEpgv2Enabled,
  previewPassEnabled,
  tveEnabled,
} from 'helpers/ConfigHelper';
import { getNetworkList, getNetworksDisplayOrder } from 'helpers/ContentHelper';
import { getAuthRedirectUrl } from 'helpers/RouteHelper';
import { getTheme, webThemeIds } from 'helpers/ThemeHelper';
import { userAttributeEvents } from 'helpers/OptimizelyHelper';
import contentBlackoutTheme from 'themes/AuthContentBlackout.module.scss';
import authProfileSignInTheme from 'themes/AuthProfileSignIn.module.scss';
import authProfileSignInThemev2 from 'themes/AuthProfileSignInV2.module.scss';
import authMVPDIncomplete from 'themes/AuthMVPDIncomplete.module.scss';
import authMvpdDirectSignInRedirectTheme from 'themes/AuthMvpdDirectSignInRedirect.module.scss';
import authMVPDIframeTheme from 'themes/AuthMVPDIframe.module.scss';
import authSuccessTheme from 'themes/AuthSuccess.module.scss';
import authFailureTheme from 'themes/AuthFailure.module.scss';
import authCookiesBlockedTheme from 'themes/AuthCookiesBlocked.module.scss';
import authMVPDUpsellRedirectingTheme from 'themes/AuthMVPDUpsellRedirecting.module.scss';
import upsellErrorTheme from 'themes/AuthUpsellError.module.scss';
import signInIdentifierV2Theme from 'themes/AuthSignInV2.module.scss';
import signInIdentifierTheme from 'themes/styleguide/nation/Auth/SignIn.module.scss';
import MVPDRetyTheme from 'themes/styleguide/nation/Auth/MVPDRetry.module.scss';
import MVPDSearchTheme from 'themes/AuthMVPDSearch.module.scss';
import authLoadingTheme from 'themes/styleguide/nation/Auth/Loading.module.scss';
import authNotEntitledTheme from 'themes/styleguide/nation/Auth/NotEntitled.module.scss';
import previewPassWelcomeTheme from 'themes/styleguide/nation/Auth/PreviewPassWelcome.module.scss';
import playbackErrorTheme from 'components/Auth/PlaybackError/PlaybackError.module.scss';
import AuthClientless from 'components/AuthClientless';
import UnlockUpsellOverlayStyle from 'components/AuthClientless/Overlays/UnlockUpsell/UnlockUpsell.module.scss';
import { fetchMVPDProvidersAction, setPreauthorizedResourcesAction } from 'index/AppActions';
import { FreemiumContext } from 'index/FreemiumProvider';
import { getDeviceProps } from 'helpers/utils';
import { withAuth } from '../../index/AuthProvider';

const authSharedTheme = getTheme(webThemeIds.Auth.Shared);

class AuthContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.authMVPDLogout = authMVPDLogout.bind(this);
    this.getCookiesBlockedProps = getCookiesBlockedProps.bind(this);
    this.getContentBlackoutProps = getContentBlackoutProps.bind(this);
    this.getDefaultAuthProps = getDefaultAuthProps.bind(this);
    this.getFailureProps = getFailureProps.bind(this);
    this.getGeoLocationErrorProps = getGeoLocationErrorProps.bind(this);
    this.getMvpdDirectSignInRedirectProps = getMvpdDirectSignInRedirectProps.bind(this);
    this.getMVPDIframeProps = getMVPDIframeProps.bind(this);
    this.getMVPDIncompleteProps = getMVPDIncompleteProps.bind(this);
    this.getMVPDRetryProps = getMVPDRetryProps.bind(this);
    this.getMVPDSearchProps = getMVPDSearchProps.bind(this);
    this.getMVPDStartProps = getMVPDStartProps.bind(this);
    this.getMVPDUpsellRedirectingProps = getMVPDUpsellRedirectingProps.bind(this);
    this.getNoSportsOnMobileProps = getNoSportsOnMobileProps.bind(this);
    this.getNotEntitledProps = getNotEntitledProps.bind(this);
    this.getPlaybackErrorProps = getPlaybackErrorProps.bind(this);
    this.getPreviewPassWelcomeProps = getPreviewPassWelcomeProps.bind(this);
    this.getProfilePcsErrorProps = getProfilePcsErrorProps.bind(this);
    this.getProfileSignInProps = getProfileSignInProps.bind(this);
    this.getSignInProps = getSignInProps.bind(this);
    this.getUnlockUpsellProps = getUnlockUpsellProps.bind(this);
    this.getSuccessProps = getSuccessProps.bind(this);
    this.getLoadingProps = getLoadingProps.bind(this);
    this.getUpsellConfirmProps = getUpsellConfirmProps.bind(this);
    this.getUpsellErrorProps = getUpsellErrorProps.bind(this);
  }

  render() {
    const {
      account, // Can come through new hooks
      anonymousAccount, // Can come through new hooks so unneeded if account is not account || anonymousAccount
      anonymousId, // AnonymousId === ProfileId === AccessToken === AuthorizationToken just use accessToken
      anonymousLogin, // This should be handled in the new auth context hook now
      background, // Used in Video Container
      blackoutConfig, // Just call getBlackoutConfig() directly
      blackoutDefinitions, // Used
      blockMVPDIncomplete, // Used
      channel, // Used in Video Container
      checkBlackouts, // Used in Video Container
      checkBuythroughScenarios: checkBuythroughScenariosProp, // Used false from video container and true from app
      closeAccount, // Does nothing
      contentNetworks, // Passed from Video Player Global to Video Container
      contentRating, // Passed from Video Player Global to Video Container
      contentSKU, // Passed from Video Player Global to Video Container
      deviceId, // Can be interpreted from account
      entitlementIssues,
      fetchMVPDProviders,
      geoLocationDeclined,
      geoLocationPrimaryActionCallback,
      geoLocationTimeoutFail,
      getDesignatedMarketingAreaData,
      guid,
      isLive,
      isLiveRestart,
      isMobile,
      isTablet,
      mvpdExpired,
      mvpdLogin,
      mvpdLoginProfile, // Always false so does nothing
      mvpdProviders,
      mvpdSignInTrigger,
      network,
      onAuthCheckComplete,
      onOverlayClose,
      onShowProvidersCallback,
      openSignIn,
      openSignUp,
      pageViewEvent,
      preauthorizedResources,
      previewPassExpired,
      previewPassSignIn,
      profile,
      profileToken, // AnonymousId === ProfileId === AccessToken === AuthorizationToken just use accessToken
      provider,
      releaseURL,
      requestSignIn,
      requiresAuth,
      requiresAuthLive,
      resetShowCookiesBlocked,
      resetSingleProviderSignInClicked,
      resumeWithProfileConfig,
      savePercentWatched,
      selectedChannel,
      setAccountCloseIgnoreRedirect,
      setAuthError,
      setAuthorizationToken,
      setDelayPlayback,
      setIsAuthenticated,
      setMVPDSignInTrigger,
      setMVPDToAppState,
      setOverlay,
      setPreAuthCheckComplete,
      setPreauthorizedResources,
      setProvider,
      setToken,
      setTriggerAuthCheck,
      setVideoOverlay,
      shouldCheckPreviewPass, // Used false from video container and true from app
      showCookiesBlocked,
      showEmailForm,
      showLoaderAfterAuth,
      showNoSportsOnMobileError, // Always false can remove
      showProviders,
      showResumeCTA,
      showSignIn,
      showUpsellProgressBar,
      singleProviderId,
      singleProviderSignInClicked,
      subscriptionPaused,
      topMVPDProviders,
      triggerAuthCheck,
      type,
      upsellRedirectOnProviderClick,
      videoId,
      videoType,
      waitForUpsell,
      waitForVideo,
    } = this.props;
    const { requestorId } = window.ExternalConfig;
    const mvpdId = _.get(provider, 'adobePassId');
    const networkList = getNetworkList();
    const networkDisplayOrder = getNetworksDisplayOrder();
    const profileId = _.get(account, 'profileId');
    const authenticationRedirectUrl = getAuthRedirectUrl();

    const sharedTheme = authSharedTheme;
    const defaultAuthProps = this.getDefaultAuthProps({
      account,
      preauthorizedResources,
      previewPassExpired,
      previewPassSignIn,
    });
    const signInIdentifier = isEpgv2Enabled() ? signInIdentifierV2Theme : signInIdentifierTheme;
    const signInProps = this.getSignInProps({
      theme: signInIdentifier,
      account,
      previewPassExpired,
      previewPassSignIn,
    });
    const unlockUpsellProps = this.getUnlockUpsellProps({
      Style: UnlockUpsellOverlayStyle,
      account,
      previewPassExpired,
      previewPassSignIn,
    });
    const profileSignInProps = this.getProfileSignInProps({
      theme: isEpgv2Enabled() ? authProfileSignInThemev2 : authProfileSignInTheme,
      account,
    });
    const mvpdIframeProps = this.getMVPDIframeProps({
      theme: authMVPDIframeTheme,
      sharedTheme,
    });
    const mvpdStartProps = this.getMVPDStartProps({
      accessToken: profileToken,
      account,
      deviceId,
      mvpdProviders,
      mvpdLogin,
      onOverlayClose,
      previewPassExpired,
      previewPassSignIn,
      profile,
      setAuthorizationToken,
      setMVPDToAppState,
      sharedTheme,
      showEmailForm,
      showUpsellProgressBar,
      topMVPDProviders,
    });
    const mvpdIncompleteProps = this.getMVPDIncompleteProps({
      theme: authMVPDIncomplete,
    });
    const mvpdRetryProps = this.getMVPDRetryProps({
      sharedTheme,
      theme: MVPDRetyTheme,
    });
    const mvpdSearchProps = this.getMVPDSearchProps({
      theme: MVPDSearchTheme,
      sharedTheme,
      topMVPDProviders,
      mvpdProviders,
      isMobile,
      account,
      onOverlayClose,
      previewPassExpired,
      previewPassSignIn,
    });
    const successProps = this.getSuccessProps({
      theme: authSuccessTheme,
      sharedTheme,
      networkList: networkDisplayOrder,
      account,
    });
    const mvpdDirectSignInRedirectProps = this.getMvpdDirectSignInRedirectProps({
      onOverlayClose,
      sharedTheme,
      theme: authMvpdDirectSignInRedirectTheme,
    });
    const mvpdUpsellRedirectingProps = this.getMVPDUpsellRedirectingProps({
      theme: authMVPDUpsellRedirectingTheme,
      sharedTheme,
      onOverlayClose,
    });
    const loadingProps = this.getLoadingProps({
      theme: authLoadingTheme,
      sharedTheme,
      onOverlayClose,
    });
    const failureProps = this.getFailureProps({
      theme: authFailureTheme,
      sharedTheme,
      account,
    });
    const playbackErrorProps = this.getPlaybackErrorProps({
      theme: playbackErrorTheme,
      sharedTheme,
      provider,
    });
    const previewPassWelcomeProps = this.getPreviewPassWelcomeProps({
      theme: previewPassWelcomeTheme,
    });

    const geoLocationProps = this.getGeoLocationErrorProps({
      sharedTheme,
      isGeolocationEnabled: geolocationEnabled(),
    });
    const noSportsOnMobileProps = this.getNoSportsOnMobileProps({
      theme: playbackErrorTheme,
      sharedTheme,
    });
    const notEntitledProps = this.getNotEntitledProps({
      sharedTheme,
      theme: authNotEntitledTheme,
      onLogout: this.authMVPDLogout,
    });
    const cookiesBlockedProps = this.getCookiesBlockedProps({
      options: {
        theme: authCookiesBlockedTheme,
        sharedTheme,
        showCookiesBlocked,
        resetShowCookiesBlocked,
      },
    });
    const profilePcsErrorProps = this.getProfilePcsErrorProps({
      theme: playbackErrorTheme,
      sharedTheme,
    });
    const contentBlackoutProps = this.getContentBlackoutProps({
      theme: contentBlackoutTheme,
      sharedTheme,
    });
    const previewPassProps = {
      previewPassExpired,
      previewPassSignIn,
      showProviders,
      previewPassOnOpenOverlay: () => {
        const action = 'authentication_start';
        const authStartSource = 'preview pass expiration';
        const onboardSrc = 'preview pass:expiration';
        setTealiumSourceAuth(authStartSource);
        const data = setTealiumProfileId(profileId, {
          auth_start_source: authStartSource,
          onboarding_start_source: onboardSrc,
          onboarding_action: TealiumOnboardingActions.COMPLETE,
          action,
          platform_version: BUILD_VERSION,
          ...getCommonTealiumProps(),
        });
        callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
      },
    };
    const upsellConfirmProps = this.getUpsellConfirmProps({
      account,
      onOverlayClose,
      sharedTheme,
      theme: upsellErrorTheme,
      profileId,
    });
    const upsellErrorProps = this.getUpsellErrorProps({
      onAuthCheckComplete,
      onOverlayClose,
      sharedTheme,
      theme: upsellErrorTheme,
    });
    const authTrackEventsProps = getSegmentAuthTrackEvents();
    const authEndpointConfig = _.get(window.ExternalConfig, 'api.auth');
    const packageId = _.get(window.ExternalConfig, 'upsellPromo.package');
    const apiKey = helperGetApiKey();
    const authNetworksConfig = getAuthNetworksConfig();
    const checkBuythroughScenarios = checkBuythroughScenariosProp;
    const backgroundUrl = getAuthBackgroundImage();
    let backgroundStyle = background ? `url(${background})` : '#000';
    const backgroundGradient = '';
    const signInBackground = getAuthSignInBackgroundImage();
    if (signInBackground) backgroundStyle = `url(${signInBackground})`;
    const { model, osName } = getDeviceProps();
    const MVPDEvents = getSegmentMVPDEvents();
    const { AUTHENTICATION_STARTED } = TrackEvents;
    const { foxProfileState } = userAttributeEvents();
    const { [AUTHENTICATION_STARTED]: tealiumAuthenticationStarted } = getAccountAnalyticsEvents({
      profileId,
      foxProfileState,
    });
    const authProps = {
      ...contentBlackoutProps,
      ...cookiesBlockedProps,
      ...defaultAuthProps,
      ...failureProps,
      ...geoLocationProps,
      ...mvpdDirectSignInRedirectProps,
      ...mvpdIframeProps,
      ...mvpdIncompleteProps,
      ...mvpdRetryProps,
      ...mvpdSearchProps,
      ...mvpdStartProps,
      ...noSportsOnMobileProps,
      ...notEntitledProps,
      ...playbackErrorProps,
      ...previewPassWelcomeProps,
      ...previewPassProps,
      ...profilePcsErrorProps,
      ...profileSignInProps,
      ...signInProps,
      ...unlockUpsellProps,
      ...successProps,
      ...loadingProps,
      ...mvpdUpsellRedirectingProps,
      ...authTrackEventsProps,
      ...upsellConfirmProps,
      ...upsellErrorProps,
      ...MVPDEvents,
      apiKey,
      authEndpointConfig,
      authenticationRedirectUrl,
      account,
      anonymousAccount,
      anonymousId,
      anonymousLogin,
      authNetworksConfig,
      authorizationToken: profileToken,
      backgroundInline: {
        background: `${backgroundGradient} padding-box padding-box, ${backgroundStyle} center center/cover no-repeat`,
      },
      backgroundUrl,
      blackoutConfig,
      blackoutDefinitions,
      blockMVPDIncomplete,
      blockSuccessScreen: true,
      buythroughDataCookieName: BUYTHROUGH_DATA_COOKIE_NAME,
      buythroughEnabled: true,
      channel,
      checkBuythroughScenarios,
      closeAccount,
      contentNetworks,
      contentRating,
      contentSKU,
      deviceId,
      entitlementIssues,
      fetchMVPDProviders,
      geoLocationDeclined,
      geoLocationPrimaryActionCallback,
      geoLocationTimeoutFail,
      getDesignatedMarketingAreaData,
      getNetworkList,
      guid,
      inRequestorID: requestorId,
      isCookiesBlockedError: showCookiesBlocked,
      isLive,
      isLiveRestart,
      isMobile,
      isTablet,
      isTveEnabled: tveEnabled(),
      model,
      mvpd: mvpdId,
      mvpdCookieName: MVPD_COOKIE_NAME,
      mvpdExpired,
      mvpdLoginProfile,
      mvpdProviders,
      mvpdSignInTrigger,
      network,
      networkList,
      onAuthCheckComplete,
      onResumeWithProfileShownAnalytics: () => {
        handleAnalytics({
          event: Events.PROFILE_UPSELL_PRESENTED,
          payload: { utagAccountSource: 'upsell' },
        });
      },
      options: adobeAuthOptions,
      osName,
      packageId,
      pageViewEvent,
      playerLoadingImage: background,
      preauthorizedResources,
      previewPassCookieName: MVPD_COOKIE_NAME,
      previewPassEnabled: previewPassEnabled(),
      provider,
      redirect_url: document.location.href,
      releaseURL,
      renderVideoProviderSearch: (props = {}) => (
        <VideoProviderSearch mvpdProviders={mvpdProviders} {...props} />
      ),
      requestSignIn,
      requiresAuth,
      requiresAuthLive,
      resetShowCookiesBlocked,
      resetSingleProviderSignInClicked,
      resumeWithProfileConfig,
      resumeWithProfileOpenSignIn: () => {
        savePercentWatched();
        openSignIn({ utagAccountSource: 'upsell' });
        setAccountCloseIgnoreRedirect(true);
      },
      resumeWithProfileOpenSignUp: () => {
        savePercentWatched();
        openSignUp({ utagAccountSource: 'upsell' });
        setAccountCloseIgnoreRedirect(true);
      },
      selectedChannel,
      setAuthError,
      setAuthorizationToken,
      setDelayPlayback,
      setIsAuthenticated,
      setMVPDSignInTrigger,
      setMVPDToAppState,
      setOverlay,
      setPreAuthCheckComplete,
      setPreauthorizedResources,
      setProvider,
      setSegmentPageStartSource,
      setToken,
      setTriggerAuthCheck,
      setVideoOverlay,
      sharedTheme: authSharedTheme,
      shouldCheckPreviewPass,
      showLoaderAfterAuth,
      showNoSportsOnMobileError, // Always false can remove
      showProvidersHandler: onShowProvidersCallback,
      showResumeCTA,
      showSignIn,
      singleProviderId,
      singleProviderSignInClicked,
      subscriptionPaused,
      successCookieName: SUCCESS_COOKIE_NAME,
      tealiumAuthenticationStarted,
      topMVPDProviders,
      triggerAuthCheck,
      type,
      upsellRedirectOnProviderClick,
      url: AccessEnablerUrl,
      useMVPDRetry: true,
      videoId,
      videoType,
    };
    const blackoutEnabled = _.get(blackoutConfig, 'enabled');
    const waitForBlackoutData = blackoutEnabled && checkBlackouts && !blackoutDefinitions;
    const waitForAuth = !profileToken;

    const shouldWait = waitForBlackoutData || waitForVideo || waitForAuth || waitForUpsell;

    if (shouldWait) return null;

    return (
      <FreemiumContext.Consumer>
        {({ isFreemium }) => (
          <AuthClientless
            data-test="auth-container-clientless-auth"
            isFreemium={isFreemium}
            {...authProps}
          />
        )}
      </FreemiumContext.Consumer>
    );
  }
}

const mapStateToProps = ({ app }) => {
  const { preauthorizedResources, mvpdProviders, topMVPDProviders } = app;

  return {
    preauthorizedResources,
    mvpdProviders,
    topMVPDProviders,
  };
};

const mapDispatchToProps = {
  fetchMVPDProviders: fetchMVPDProvidersAction,
  setPreauthorizedResources: setPreauthorizedResourcesAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(withAuth(AuthContainer));

AuthContainer.defaultProps = {
  account: null,
  anonymousAccount: null,
  anonymousId: null,
  background: '',
  blackoutConfig: null,
  blackoutDefinitions: null,
  blockMVPDIncomplete: null,
  blockSuccessScreen: null,
  closeAccount: noop,
  channel: null,
  checkBlackouts: false,
  checkBuythroughScenarios: false,
  contentNetworks: [],
  contentRating: null,
  contentSKU: null,
  deviceId: null,
  entitlementIssues: null,
  geoLocationDeclined: null,
  geoLocationTimeoutFail: false,
  geoLocationPrimaryActionCallback: noop,
  getDesignatedMarketingAreaData: noop,
  guid: null,
  isLive: false,
  isLiveRestart: null,
  isMobile: false,
  isTablet: false,
  mvpdExpired: false,
  mvpdLogin: noop,
  mvpdLoginProfile: false,
  mvpdProviders: [],
  mvpdSignInTrigger: false,
  network: null,
  onAuthCheckComplete: noop,
  onOverlayClose: noop,
  onShowProvidersCallback: noop,
  openSignIn: noop,
  openSignUp: noop,
  pageViewEvent: noop,
  preauthorizedResources: [],
  previewPassExpired: false,
  previewPassSignIn: false,
  profileToken: null,
  provider: null,
  releaseURL: null,
  requestSignIn: false,
  requiresAuth: null,
  requiresAuthLive: null,
  resetShowCookiesBlocked: noop,
  resetSingleProviderSignInClicked: noop,
  resumeWithProfileConfig: null,
  savePercentWatched: noop,
  selectedChannel: null,
  setAccountCloseIgnoreRedirect: noop,
  setAuthError: noop,
  setDelayPlayback: noop,
  setMVPDSignInTrigger: noop,
  setMVPDToAppState: noop,
  setPreAuthCheckComplete: noop,
  setToken: noop,
  setTriggerAuthCheck: noop,
  setVideoOverlay: noop,
  shouldCheckPreviewPass: false,
  showCookiesBlocked: false,
  showEmailForm: false,
  showLoaderAfterAuth: false,
  showNoSportsOnMobileError: false,
  showProviders: false,
  showResumeCTA: false,
  showSignIn: false,
  showUpsellProgressBar: false,
  singleProviderId: null,
  singleProviderSignInClicked: false,
  topMVPDProviders: [],
  triggerAuthCheck: false,
  type: '',
  upsellRedirectOnProviderClick: false,
  videoId: null,
  videoType: null,
  waitForUpsell: null,
  waitForVideo: false,
};

AuthContainer.propTypes = {
  account: PropTypes.shape({}),
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func.isRequired,
  background: PropTypes.string,
  blackoutConfig: PropTypes.shape({}),
  blackoutDefinitions: PropTypes.shape({}),
  blockMVPDIncomplete: PropTypes.bool,
  blockSuccessScreen: PropTypes.bool,
  channel: PropTypes.string,
  checkBlackouts: PropTypes.bool,
  checkBuythroughScenarios: PropTypes.bool,
  closeAccount: PropTypes.func,
  contentNetworks: PropTypes.arrayOf(PropTypes.string),
  contentRating: PropTypes.string,
  contentSKU: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  deviceId: PropTypes.string,
  entitlementIssues: PropTypes.arrayOf(PropTypes.shape({})),
  fetchMVPDProviders: PropTypes.func.isRequired,
  geoLocationDeclined: PropTypes.bool,
  geoLocationTimeoutFail: PropTypes.bool,
  geoLocationPrimaryActionCallback: PropTypes.func,
  getDesignatedMarketingAreaData: PropTypes.func,
  guid: PropTypes.string,
  isLive: PropTypes.bool,
  isLiveRestart: PropTypes.bool,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  mvpdExpired: PropTypes.bool,
  mvpdLogin: PropTypes.func,
  mvpdLoginProfile: PropTypes.bool,
  mvpdProviders: PropTypes.arrayOf(PropTypes.shape({})),
  mvpdSignInTrigger: PropTypes.bool,
  network: PropTypes.string,
  onAuthCheckComplete: PropTypes.func,
  onOverlayClose: PropTypes.func,
  onShowProvidersCallback: PropTypes.func,
  openSignIn: PropTypes.func,
  openSignUp: PropTypes.func,
  pageViewEvent: PropTypes.func,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  previewPassExpired: PropTypes.bool,
  previewPassSignIn: PropTypes.bool,
  profile: PropTypes.shape({}),
  profileToken: PropTypes.string,
  provider: PropTypes.shape({}),
  releaseURL: PropTypes.string,
  requestSignIn: PropTypes.bool,
  requiresAuth: PropTypes.bool,
  requiresAuthLive: PropTypes.bool,
  resetShowCookiesBlocked: PropTypes.func,
  resetSingleProviderSignInClicked: PropTypes.func,
  resumeWithProfileConfig: PropTypes.shape({}),
  savePercentWatched: PropTypes.func,
  selectedChannel: PropTypes.number,
  setAccountCloseIgnoreRedirect: PropTypes.func,
  setAuthError: PropTypes.func,
  setAuthorizationToken: PropTypes.func.isRequired,
  setDelayPlayback: PropTypes.func,
  setIsAuthenticated: PropTypes.func.isRequired,
  setMVPDSignInTrigger: PropTypes.func,
  setMVPDToAppState: PropTypes.func,
  setOverlay: PropTypes.func.isRequired,
  setPreAuthCheckComplete: PropTypes.func,
  setPreauthorizedResources: PropTypes.func.isRequired,
  setProvider: PropTypes.func.isRequired,
  setToken: PropTypes.func,
  setTriggerAuthCheck: PropTypes.func,
  setVideoOverlay: PropTypes.func,
  shouldCheckPreviewPass: PropTypes.bool,
  showCookiesBlocked: PropTypes.bool,
  showEmailForm: PropTypes.bool,
  showLoaderAfterAuth: PropTypes.bool,
  showNoSportsOnMobileError: PropTypes.bool,
  showProviders: PropTypes.bool,
  showResumeCTA: PropTypes.bool,
  showSignIn: PropTypes.bool,
  showUpsellProgressBar: PropTypes.bool,
  singleProviderId: PropTypes.string,
  singleProviderSignInClicked: PropTypes.bool,
  subscriptionPaused: PropTypes.bool,
  theme: PropTypes.shape({}),
  topMVPDProviders: PropTypes.arrayOf(PropTypes.shape({})),
  triggerAuthCheck: PropTypes.bool,
  type: PropTypes.string,
  upsellRedirectOnProviderClick: PropTypes.bool,
  videoId: PropTypes.string,
  videoType: PropTypes.string,
  waitForUpsell: PropTypes.bool,
  waitForVideo: PropTypes.bool,
};
